import type { TaskStep } from './taskService'
export type TimeWindow = 'morning' | 'afternoon' | 'evening' | 'night'
export type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

export interface BreakdownActionItem {
  id: number
  title: string
  description?: string
  status: string
  progress: number
  priority: string
  category: string
  created_at?: string
}

export type FollowThroughMode = 'hard_window' | 'soft_window' | 'all_day'
export type NagStyle = 'gentle' | 'steady' | 'persistent' | 'strict'

export type HabitExecutionMode = 'atomic' | 'routine_bundle' | 'formation_journey' | 'misclassified'

export interface Habit {
  id: number | string
  habit_definition_id?: number
  title: string
  description: string
  /** The lifecycle lane, written by rationing / admission (active, parked,
   *  committed, …) — a free string on prod, never the formation stage. */
  status: string
  habit_status?: string
  target_cadence?: 'daily' | 'weekly' | 'biweekly' | 'monthly'
  preferred_days?: Weekday[]
  preferred_time_window?: TimeWindow
  duration_minutes?: number
  rrule?: string
  scheduled_times_local?: string[]
  schedule_start_date?: string
  preferred_start_time?: string | null
  minimum_viable_repetition?: string
  cue_or_anchor?: string
  progress: number
  priority: 'low' | 'medium' | 'high' | 'urgent'
  life_area?: string
  created_at: string
  updated_at?: string
  parent_type?: 'dream' | 'goal' | null
  parent_id?: number | null
  parent_title?: string | null
  has_ai_breakdown?: boolean
  habit_execution_mode?: HabitExecutionMode
  ai_classification_confidence?: number
  classification_reason?: string
  routine_template_id?: number | null
  habit_journey_plan_id?: number | null
  nag_enabled?: boolean
  follow_through_mode?: FollowThroughMode
  nag_window_minutes?: number
  nag_interval_minutes?: number
  nag_max_per_window?: number
  nag_style?: NagStyle
  allow_tiny_step_rescue?: boolean
  allow_time_adjustment_suggestion?: boolean
  deep_support_enabled?: boolean
  is_active?: boolean
  paused_until?: string | null
  pause_reason_codes?: string[]
  archived_at?: string | null
  archive_source?: string | null
  archive_reason?: string | null
  /** Habits UX pass 2026-09-06: the list shape for life areas, the direct
   *  parent as the task list names it, and the definition's zone. */
  life_areas?: string[] | null
  parent?: { type: 'goal' | 'dream' | 'skill' | 'aspiration'; id: number; title: string | null; life_area?: string | null } | null
  timezone?: string | null
  parent_goal_id?: number | null
  parent_skill_id?: number | null
  parent_dream_id?: number | null
  /** The habit's own short list — what each time consists of (2026-09-06). */
  steps?: TaskStep[] | null
}

/** One day on a habit's board strip. */
export type HabitOutcome = 'done' | 'skipped' | 'missed' | 'pending'

/** A habit's card on the list page (GET /api/hierarchy/habits/board). */
export interface HabitBoardCard {
  today: { id: number; status: string; outcome: HabitOutcome; at: string | null; scheduled_at: string | null; steps_done?: string[] } | null
  next: { date: string; at: string | null } | null
  last_14: Array<{ date: string; outcome: HabitOutcome | null }>
  done_30: number
  chances_30: number
  streak: number
  best_streak: number
  last_done: string | null
  words: string
}

export interface HabitBoard {
  today: string
  habits: Record<string, HabitBoardCard>
}

export interface HabitClassificationResult {
  classification: HabitExecutionMode
  confidence: number
  reason: string
  recommended_structure: {
    type: string
    tasks?: { title: string; description: string; estimated_duration_minutes: number; order: number }[]
    steps?: { title: string; description: string; estimated_duration_minutes: number; order: number }[]
    suggested_type?: string
    error?: string
  }
}

export interface HabitJourneyStep {
  step_id: string
  order: number
  title: string
  description: string
  estimated_duration_minutes: number
  status: 'locked' | 'unlocked' | 'completed'
  task_id: number | null
  unlocked_at: string | null
  completed_at: string | null
}

export interface HabitJourneyPlan {
  journey_plan_id: number
  habit_id: number
  status: 'active' | 'completed' | 'archived'
  current_step_index: number
  active_task_id: number | null
  steps: HabitJourneyStep[]
  created_at?: string
  updated_at?: string
}

export interface HabitRoutineTemplate {
  id: number
  title: string
  tasks: { template_task_id: string; order: number; title: string; description: string; estimated_duration_minutes: number }[]
}

export interface CreateHabitRequest {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high'
  life_area?: string
  life_areas?: string[]
  habit_status?: string
  target_cadence?: string
  preferred_days?: Weekday[]
  preferred_time_window?: TimeWindow
  preferred_start_time?: string
  duration_minutes?: number
  rrule?: string
  scheduled_times_local?: string[]
  schedule_start_date?: string
  minimum_viable_repetition?: string
  cue_or_anchor?: string
  parent_goal_id?: number
  parent_skill_id?: number | null
  parent_dream_id?: number
  // Follow-through fields
  nag_enabled?: boolean
  follow_through_mode?: FollowThroughMode
  nag_window_minutes?: number
  nag_interval_minutes?: number
  nag_max_per_window?: number
  nag_style?: NagStyle
  allow_tiny_step_rescue?: boolean
  allow_time_adjustment_suggestion?: boolean
  deep_support_enabled?: boolean
}

export interface UpdateHabitRequest {
  title?: string
  description?: string | null
  status?: string
  habit_status?: string
  life_areas?: string[]
  parent_goal_id?: number | null
  parent_skill_id?: number | null
  parent_dream_id?: number | null
  steps?: TaskStep[]
  target_cadence?: string
  preferred_days?: Weekday[]
  preferred_time_window?: TimeWindow
  preferred_start_time?: string
  duration_minutes?: number
  rrule?: string
  scheduled_times_local?: string[]
  schedule_start_date?: string
  minimum_viable_repetition?: string | null
  cue_or_anchor?: string | null
  progress?: number
  priority?: 'low' | 'medium' | 'high'
  life_area?: string
  // Follow-through fields
  nag_enabled?: boolean
  follow_through_mode?: FollowThroughMode
  nag_window_minutes?: number
  nag_interval_minutes?: number
  nag_max_per_window?: number
  nag_style?: NagStyle
  allow_tiny_step_rescue?: boolean
  allow_time_adjustment_suggestion?: boolean
  deep_support_enabled?: boolean
}

export class HabitService {
  private baseUrl = '/api/hierarchy/habits'

  /** `includeAll` keeps parked / held habits in (the life map's view); the default lane is active only. */
  async getHabits(includeAll = false): Promise<Habit[]> {
    const response = await fetch(includeAll ? `${this.baseUrl}?include=all` : this.baseUrl, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      throw new Error('Failed to fetch habits')
    }
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  async getHabit(id: string): Promise<Habit> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch habit with id ${id}`)
    }
    const data = await response.json()
    return data.data || data
  }

  async createHabit(habit: CreateHabitRequest): Promise<Habit> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(habit)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create habit' }))
      throw new Error(error.error || 'Failed to create habit')
    }
    const data = await response.json()
    return data.data || data
  }

  async updateHabit(id: string, updates: UpdateHabitRequest): Promise<Habit> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      throw new Error('Failed to update habit')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteHabit(id: string): Promise<{ cleanup?: Record<string, number> }> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      throw new Error('Failed to delete habit')
    }
    return response.json().catch(() => ({}))
  }

  /** The list page's one read: every habit's today / next / last 14 / run. `today` is the person's local date. */
  async getBoard(today: string): Promise<HabitBoard> {
    const response = await fetch(`${this.baseUrl}/board?today=${encodeURIComponent(today)}`, { credentials: 'include' })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || body.success === false) throw new Error(body.error || 'Could not load the habits board')
    return body.data as HabitBoard
  }

  /** Done / not today / undo for one habit on the person's local day; `stepsDone` ticks the habit's steps for that day. */
  async markToday(id: number | string, outcome: 'done' | 'skipped' | 'pending' | null, day: string, note?: string, stepsDone?: string[]): Promise<{ occurrence_id: number | null; board: HabitBoardCard | null }> {
    const response = await fetch(`${this.baseUrl}/${id}/today`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome: outcome || undefined, day, note: note || undefined, steps_done: stepsDone }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || body.success === false) throw new Error(body.error || 'Could not record today')
    return body.data
  }

  /** AI breakdown into action items (same journey-engine flow as tasks). */
  async breakdownHabit(habitId: number): Promise<{
    action_items: BreakdownActionItem[]
    revised_total_duration_minutes?: number
    original_estimated_duration?: number
  }> {
    const response = await fetch(`${this.baseUrl}/${habitId}/breakdown`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to generate breakdown' }))
      throw new Error(error.error || 'Failed to generate breakdown')
    }
    return response.json()
  }

  async getFirstStep(habitId: number): Promise<{
    action_item: BreakdownActionItem
    revised_total_duration_minutes?: number
    original_estimated_duration?: number
  }> {
    const response = await fetch(`${this.baseUrl}/${habitId}/first-step`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to generate first step' }))
      throw new Error(error.error || 'Failed to generate first step')
    }
    return response.json()
  }

  async previewStructure(habitId: number | string): Promise<HabitClassificationResult> {
    const response = await fetch(`${this.baseUrl}/${habitId}/preview-structure`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to classify habit' }))
      throw new Error(err.error || 'Failed to classify habit')
    }
    return response.json()
  }

  async acceptStructure(
    habitId: number | string,
    classification: HabitExecutionMode,
    structure: Record<string, unknown>,
    reason?: string,
    confidence?: number,
  ): Promise<Record<string, unknown> & { created_task?: { id: number; title: string } }> {
    const response = await fetch(`${this.baseUrl}/${habitId}/accept-structure`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classification, structure, reason, confidence }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to accept structure' }))
      throw new Error(err.error || 'Failed to accept structure')
    }
    return response.json()
  }

  async getStructure(habitId: number | string): Promise<Record<string, unknown>> {
    const response = await fetch(`${this.baseUrl}/${habitId}/structure`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to fetch structure' }))
      throw new Error(err.error || 'Failed to fetch structure')
    }
    return response.json()
  }

  async getJourneyPlan(habitId: number | string): Promise<HabitJourneyPlan | null> {
    const response = await fetch(`${this.baseUrl}/${habitId}/journey-plan`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (response.status === 404) return null
    if (!response.ok) {
      throw new Error('Failed to fetch habit journey plan')
    }
    return response.json()
  }
}
